import { featureFlags } from "../config/featureFlags";
import { Budget } from "../types/budget/budget";
import { BudgetSummary } from "../types/budget/budget-summary";
import {
  BudgetServiceLine,
  BudgetServiceWriteResponse,
  BudgetServicesListResponse,
  CreateBudgetServiceV2Body,
  GetBudgetServiceV2Params,
  GetBudgetServicesV2Params,
  PatchBudgetServiceV2Body,
  PatchBudgetServicesBatchBody,
  PatchBudgetServicesBatchResponse,
} from "../types/budget/budget-service-v2";
import type {
  BudgetTotalsResponse,
  GetBudgetTotalsParams,
  ServiceWriteTotalsAttachment,
} from "../types/budget/budget-totals";
import { ServiceData } from "../types/budget/service-item";
import { stripTotalsTreesFromBudgetPayload } from "../utils/budget-server-totals";
import { api } from "./apiClient";

/**
 * Mirror of `featureFlags.useV2ServiceWrites` (`REACT_APP_USE_V2_SERVICE_WRITES`).
 *
 * When true (FE-P3-03/04): editor autosave flushes service field edits via
 * {@link BudgetService.patchBudgetServicesBatchV2}, create/delete/move via
 * POST/DELETE (moves = delete+create), then a slim v1 PUT for config
 * (no `budgetServices`). With `USE_SERVER_TOTALS` (FE-P4-02), that slim PUT
 * is skipped after batch/structural when config is clean — server totals come
 * from write-response slices. Header/tax edits still use full v1 PUT.
 * When false: full-document v1 `PUT` only (Phase 0–2 behavior).
 */
export function isV2ServiceWritesEnabled(): boolean {
  return featureFlags.useV2ServiceWrites;
}

/** Mirror of `featureFlags.useServerTotals` (`REACT_APP_USE_SERVER_TOTALS`). */
export function isServerTotalsEnabled(): boolean {
  return featureFlags.useServerTotals;
}

interface UploadFileParams {
  id: string;
  type: string;
  supplierId: string;
  calcOption: string;
  file: FormData;
}

interface UploadFileResponse {
  success: boolean;
  file: {
    name: string;
    path: string;
    mimeType: string;
    size: number;
    uploadedAt: string;
  };
}

export class BudgetService {
  static async getBudgetList(projectId: string): Promise<any> {
    return api
      .get("/budget/get?", {
        params: {
          project: projectId,
          _t: Date.now(), // Cache buster
        },
      })
      .then((response) => response.data);
  }

  static async getBudgetSummaryList(
    projectId: string
  ): Promise<BudgetSummary[]> {
    return api
      .get("/v2/budget", {
        params: {
          project: projectId,
        },
      })
      .then((response) => response.data);
  }

  /**
   * Full budget document by id (`GET /budget/get?id=`).
   * Used to hydrate the editor when the list row is a summary-only DTO.
   */
  static async getBudgetById(budgetId: string): Promise<Budget> {
    const data = await api
      .get("/budget/get?", {
        params: {
          id: budgetId,
          _t: Date.now(),
        },
      })
      .then((response) => response.data);

    if (Array.isArray(data)) {
      const match =
        data.find((row: Budget) => row?._id === budgetId) ?? data[0];
      if (!match) {
        throw new Error(`Budget not found: ${budgetId}`);
      }
      return match;
    }

    if (!data?._id) {
      throw new Error(`Budget not found: ${budgetId}`);
    }
    return data;
  }

  /**
   * Budget shell / header without `budgetServices` (`GET /v2/budget/:id`).
   * With `useV2BudgetDetail`, combined with {@link getBudgetServicesV2} in
   * `fetchBudgetDetail` (FE-P3-01). Flag off: not called from the editor path.
   */
  static async getBudgetShell(
    budgetId: string
  ): Promise<Omit<Budget, "budgetServices"> & { _id: string }> {
    return api.get(`/v2/budget/${budgetId}`).then((response) => response.data);
  }

  /**
   * Server totals tree slice (`GET /v2/budget/:id/totals`, BE-P4-04 / FE-P4-01).
   * Prefer write-response `?includeTotals=true` slices when available; use this
   * for bootstrap, stale `totalsVersion` refetch, or post-slim-PUT sync.
   */
  static async getBudgetTotals(
    budgetId: string,
    params: GetBudgetTotalsParams
  ): Promise<BudgetTotalsResponse> {
    return api
      .get(`/v2/budget/${budgetId}/totals`, { params })
      .then((response) => response.data);
  }

  static async uploadFile(
    id: string,
    type: string,
    supplierId: string,
    calcOption: string,
    file: FormData
  ): Promise<{ data: UploadFileResponse }> {
    return api.post(
      `budget/upload/${id}/${type}/${supplierId}/${calcOption}`,
      file,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  }
  static async deleteFile(
    id: string,
    type: string,
    supplierId: string,
    calcOption: string,
    fileHash: string
  ): Promise<any> {
    return api.delete(
      `budget/delete-file/${id}/${type}/${supplierId}/${calcOption}/${fileHash}`
    );
  }

  static async changeFileName(
    id: string,
    type: string,
    supplierId: string,
    calcOption: string,
    fileHash: string,
    newName: string
  ): Promise<any> {
    return api.post(
      `budget/change-name/${id}/${type}/${supplierId}/${calcOption}/${fileHash}/${newName}`
    );
  }

  static async downloadFile(id: string, hash: string) {
    try {
      const res = await api.get(`budget/download/${id}/${hash}`);
      return res.data;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * @deprecated Prefer {@link getBudgetServicesV2} (`GET /v2/budget/:id/services`).
   * Legacy `/budget-items/*` path — do not use for new v2 work.
   */
  static async getServicesFromBudget(budgetId: string): Promise<any> {
    return api
      .get("/budget-items/get?", {
        params: {
          budgetId: budgetId,
        },
      })
      .then((response) => {
        console.log(response);
        return response.data;
      });
  }

  /**
   * List service lines with filters + pagination (`GET /v2/budget/:id/services`).
   * Editor bootstrap (FE-P3-01) drains pages via `fetchAllBudgetServicesV2`
   * when `useV2BudgetDetail` is on.
   */
  static async getBudgetServicesV2(
    budgetId: string,
    params: GetBudgetServicesV2Params = {}
  ): Promise<BudgetServicesListResponse> {
    return api
      .get(`/v2/budget/${budgetId}/services`, { params })
      .then((response) => response.data);
  }

  /**
   * Single service line (`GET /v2/budget/:id/services/:serviceId`).
   * Phase 2 client only — not wired to the editor (Phase 3).
   */
  static async getBudgetServiceV2(
    budgetId: string,
    serviceId: string,
    params: GetBudgetServiceV2Params = {}
  ): Promise<BudgetServiceLine> {
    return api
      .get(`/v2/budget/${budgetId}/services/${serviceId}`, { params })
      .then((response) => response.data);
  }

  /**
   * Create a service line (`POST /v2/budget/:id/services`).
   * Wired when `useV2ServiceWrites` / FE-P3-04 (add-line + move via delete+create).
   * Pass `includeTotals: true` (FE-P4-01) to attach `{ totals, totalsVersion }`.
   */
  static async createBudgetServiceV2(
    budgetId: string,
    body: CreateBudgetServiceV2Body,
    options?: { includeTotals?: boolean }
  ): Promise<BudgetServiceWriteResponse> {
    return api
      .post(`/v2/budget/${budgetId}/services`, body, {
        params: options?.includeTotals ? { includeTotals: true } : undefined,
      })
      .then((response) => response.data);
  }

  /**
   * Partial update of a service value phase (`PATCH /v2/budget/:id/services/:serviceId`).
   * Value snapshots only — group identity moves use delete+create (FE-P3-04).
   * Wired for single-op paths; editor field edits prefer batch (FE-P3-03).
   * Optional `ifMatch` → `If-Match` header (FE-P3-05 / BE-P3-04).
   * Pass `includeTotals: true` (FE-P4-01) to attach `{ totals, totalsVersion }`.
   */
  static async patchBudgetServiceV2(
    budgetId: string,
    serviceId: string,
    body: PatchBudgetServiceV2Body,
    options?: { ifMatch?: string; includeTotals?: boolean }
  ): Promise<BudgetServiceWriteResponse> {
    const headers =
      options?.ifMatch != null && options.ifMatch !== ""
        ? { "If-Match": options.ifMatch }
        : undefined;
    return api
      .patch(`/v2/budget/${budgetId}/services/${serviceId}`, body, {
        headers,
        params: options?.includeTotals ? { includeTotals: true } : undefined,
      })
      .then((response) => response.data);
  }

  /**
   * Delete a service line (`DELETE /v2/budget/:id/services/:serviceId`).
   * Default: HTTP 204, returns void. With `includeTotals: true` (FE-P4-01):
   * HTTP 200 + `{ totals, totalsVersion }` attachment.
   * Optional `ifMatch` → `If-Match` header (FE-P3-05 / BE-P3-04).
   */
  static async deleteBudgetServiceV2(
    budgetId: string,
    serviceId: string,
    options?: { ifMatch?: string; includeTotals?: boolean }
  ): Promise<ServiceWriteTotalsAttachment | void> {
    const headers =
      options?.ifMatch != null && options.ifMatch !== ""
        ? { "If-Match": options.ifMatch }
        : undefined;
    const response = await api.delete(
      `/v2/budget/${budgetId}/services/${serviceId}`,
      {
        headers,
        params: options?.includeTotals ? { includeTotals: true } : undefined,
      }
    );
    if (options?.includeTotals && response?.data) {
      return response.data as ServiceWriteTotalsAttachment;
    }
  }

  /**
   * Batch partial updates (`PATCH /v2/budget/:id/services/batch`, BE-P3-05 / D-02).
   * Wired to editor autosave when `useV2ServiceWrites` / FE-P3-03.
   * Pass `includeTotals: true` (FE-P4-01) to attach `{ totals, totalsVersion }`.
   */
  static async patchBudgetServicesBatchV2(
    budgetId: string,
    body: PatchBudgetServicesBatchBody,
    options?: { includeTotals?: boolean }
  ): Promise<PatchBudgetServicesBatchResponse> {
    return api
      .patch(`/v2/budget/${budgetId}/services/batch`, body, {
        params: options?.includeTotals ? { includeTotals: true } : undefined,
      })
      .then((response) => response.data);
  }

  static async update(id: string, budgetData: any): Promise<any> {
    const {
      __v: _version,
      isApiUpdate: _isApiUpdate,
      ...safeBudgetData
    } = budgetData || {};

    // FE-P4-02: do not send client totals trees when server owns them.
    // Local trees stay on the editor doc for display-only optimistic UX.
    const payload = featureFlags.useServerTotals
      ? stripTotalsTreesFromBudgetPayload(safeBudgetData)
      : safeBudgetData;

    return api
      .put(`/budget/update/${id}`, payload)
      .then((response) => response.data);
  }

  static async updateProjectExchange(
    projectId: string,
    exchangeData: { date: string; currency: string; value: number }
  ): Promise<any> {
    console.log("🚀 [Frontend] Chamando updateProjectExchange");
    console.log("  projectId:", projectId);
    console.log("  exchangeData:", exchangeData);

    return api
      .put(`/budget/update-project-exchange/${projectId}`, exchangeData)
      .then((response) => {
        console.log("✅ [Frontend] Resposta recebida:", response.data);
        return response.data;
      })
      .catch((error) => {
        console.error("❌ [Frontend] Erro:", error);
        throw error;
      });
  }

  static async create(data: Budget): Promise<any> {
    return api.post("/budget/create", data).then((response) => response.data);
  }
  /**
   * @deprecated Prefer {@link createBudgetServiceV2} (`POST /v2/budget/:id/services`).
   * Legacy `/budget-items/*` path — do not use for new v2 work.
   */
  static async createService(data: ServiceData): Promise<any> {
    return api
      .post("/budget-items/create", data)
      .then((response) => response.data);
  }
  /**
   * @deprecated Prefer {@link patchBudgetServiceV2} (`PATCH /v2/budget/:id/services/:serviceId`).
   * Legacy `/budget-items/*` path — do not use for new v2 work.
   */
  static async updateService(data: ServiceData): Promise<any> {
    return api
      .put(`/budget-items/update/${data._id}`, data)
      .then((response) => response.data);
  }
  /**
   * @deprecated Prefer {@link deleteBudgetServiceV2} (`DELETE /v2/budget/:id/services/:serviceId`).
   * Legacy `/budget-items/*` path — do not use for new v2 work.
   */
  static async deleteService(id: string): Promise<any> {
    return api
      .delete(`budget-items/delete/${id}`)
      .then((response) => response.data);
  }
  static async calculateService(data: Budget): Promise<any> {
    return api
      .post("/budget-items/calculate-item", data)
      .then((response) => response.data);
  }

  static async calculateBudgetTotals(data: any): Promise<any> {
    return api
      .post("/budget-total/calculate-total", data)
      .then((response) => response.data);
  }
  static async exportXLX({
    ids,
    exportType,
    exportMode,
    sortOrder,
    view,
  }: {
    ids: string[];
    exportType: string;
    exportMode?: string;
    sortOrder: string;
    /** #MMV-2582: "BY_SUPPLIER" (padrão) ou "BY_GROUP" -- estrutura atual por fornecedor vs. a nova por agrupamento. */
    view?: string;
  }): Promise<any> {
    return api
      .get(`/export-budget/generate-cost-estimates`, {
        responseType: "arraybuffer",
        params: { ids, exportType, exportMode, sortOrder, view },
      })
      .then((response) => {
        return response?.data;
      });
  }

  static async exportNegotiationXLX(exportInfo: {
    exportType: string;
    exportMode?: string;
    sortOrder: string;
    /** #MMV-2582: "BY_SUPPLIER" (padrão) ou "BY_GROUP". */
    view?: string;
    budget?: { budgetId: string; negotiationVersion: number };
    budgets?: { budgetId: string; negotiationVersion: number }[];
  }): Promise<any> {
    return api
      .post(
        `/export-budget-cost-estimates-negotiation`,
        {
          exportInfo,
        },
        {
          responseType: "arraybuffer",
        }
      )
      .then((response) => {
        return response?.data;
      });
  }

  static async exportApprovedXLX(exportInfo: {
    exportType: string;
    exportMode?: string;
    sortOrder: string;
    /** #MMV-2582: "BY_SUPPLIER" (padrão) ou "BY_GROUP". */
    view?: string;
    budgetId?: string;
    budgetIds?: string[];
  }): Promise<any> {
    return api
      .post(
        `/export-budget-cost-estimates-approved`,
        {
          exportInfo,
        },
        {
          responseType: "arraybuffer",
        }
      )
      .then((response) => {
        return response?.data;
      });
  }

  static async generateComparisonInfo({
    ids,
    exportType,
    sortOrder,
  }: {
    ids: string;
    exportType: string;
    sortOrder: string;
  }): Promise<any[]> {
    return api
      .get(`/export-budget/generate-comparison-info`, {
        params: { ids, exportType, sortOrder },
      })
      .then((response) => response?.data);
  }

  static async generateNegotiationComparisonInfo(exportInfo: {
    exportType: string;
    sortOrder: string;
    budget: { budgetId: string; negotiationVersion: number };
  }): Promise<any[]> {
    return api
      .post(
        `/export-budget-cost-estimates-negotiation/generate-comparison-info`,
        { exportInfo }
      )
      .then((response) => response?.data);
  }

  static async generateClosingComparisonInfo(exportInfo: {
    exportType: string;
    sortOrder?: string;
    budgetId: string;
  }): Promise<any[]> {
    return api
      .post(`/export-budget-cost-estimates-approved/generate-comparison-info`, {
        exportInfo,
      })
      .then((response) => response?.data);
  }
}
